import { useEffect, useMemo, useState } from "react";
import {
  fetchMarketAwarePrice,
  formatMarketAwarePriceForLegacy,
  MarketAwareProductPricing,
  peekCachedPrice,
} from "../utils/marketAwarePrices";
import { isAuMarketOverrideEnabled } from "../utils/reactifyOverride";

export interface MarketAwarePriceResult {
  enabled: boolean;
  loading: boolean;
  formattedPrice: string | null;
  formattedCompareAtPrice: string | null;
  onSale: boolean | null;
}

const EMPTY: MarketAwarePriceResult = {
  enabled: false,
  loading: false,
  formattedPrice: null,
  formattedCompareAtPrice: null,
  onSale: null,
};

export const useMarketAwarePrice = (productId: string | number | undefined): MarketAwarePriceResult => {
  const enabled = isAuMarketOverrideEnabled();
  const countryCode = window.market?.countryCode;

  const seed = useMemo(() => {
    if (!enabled || !productId || !countryCode) {
      return { hit: false, value: null as MarketAwareProductPricing | null };
    }
    return peekCachedPrice(productId, countryCode);
  }, [enabled, productId, countryCode]);

  const [pricing, setPricing] = useState<MarketAwareProductPricing | null>(seed.value);
  const [loading, setLoading] = useState<boolean>(
    enabled && Boolean(productId) && Boolean(countryCode) && !seed.hit
  );

  useEffect(() => {
    if (!enabled || !productId || !countryCode) {
      setLoading(false);
      return;
    }
    if (seed.hit) {
      setPricing(seed.value);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMarketAwarePrice(String(productId), countryCode).then((result) => {
      if (cancelled) return;
      setPricing(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, productId, countryCode, seed.hit]);

  if (!enabled) {
    return EMPTY;
  }

  return {
    enabled,
    loading,
    formattedPrice: pricing ? formatMarketAwarePriceForLegacy(pricing.price) : null,
    formattedCompareAtPrice: pricing?.compareAtPrice ? formatMarketAwarePriceForLegacy(pricing.compareAtPrice) : null,
    onSale: pricing ? Boolean(pricing.compareAtPrice) : null,
  };
};
